import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Trash2, Star, StarOff, TrendingDown, HelpCircle, ArrowLeft } from 'lucide-react';
import { useApp } from './AppContext';
import { YandexRTB } from './YandexRTB';
import type { Screen } from '../App';

interface ErrorTopScreenProps {
  onNavigate: (screen: Screen) => void;
  onBack: () => void;
}

export const ErrorTopScreen: React.FC<ErrorTopScreenProps> = ({ onNavigate, onBack }) => {
  const {
    userStats,
    questions,
    favorites,
    toggleFavorite,
    settings,
    resetQuestionErrors,
    getBackgroundClass
  } = useApp();

  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number | null>>({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [removingItems, setRemovingItems] = useState<Set<string>>(new Set());
  const [closingQuestion, setClosingQuestion] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const incorrectQuestions = Object.entries(userStats.questionErrors)
    .filter(([_, errors]) => errors > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 30)
    .map(([questionId]) => questions.find(q => q.id === questionId))
    .filter(Boolean);

  // Мемоизированные расчёты размеров шрифтов
  const actualQuestionSize = useMemo(() => {
    const baseSize = 21;
    return Math.round((settings.questionFontSize / 50) * baseSize);
  }, [settings.questionFontSize]);

  const actualAnswerSize = useMemo(() => {
    const baseSize = 18;
    return Math.round((settings.answerFontSize / 50) * baseSize);
  }, [settings.answerFontSize]);



  useEffect(() => {
    if (containerRef.current && containerWidth === null) {
      const width = containerRef.current.offsetWidth;
      setContainerWidth(width);
    }
  }, [containerWidth]);

  useEffect(() => {
    return () => {
      setSelectedAnswers({});
      setSelectedQuestion(null);
      setShowExplanation(false);
    };
  }, []);

  const handleRemoveFromIncorrect = (questionId: string) => {
    setRemovingItems(prev => new Set(prev).add(questionId));

    setTimeout(() => {
      resetQuestionErrors(questionId);
      setRemovingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });

      if (selectedQuestion === questionId) {
        setSelectedQuestion(null);
      }

      setSelectedAnswers(prev => {
        const newAnswers = { ...prev };
        delete newAnswers[questionId];
        return newAnswers;
      });
    }, 300);
  };

  const toggleQuestionDetails = (questionId: string) => {
    if (selectedQuestion === questionId) {
      setClosingQuestion(questionId);

      setSelectedAnswers(prev => ({
        ...prev,
        [questionId]: null
      }));

      setTimeout(() => {
        setSelectedQuestion(null);
        setClosingQuestion(null);
      }, 300);
    } else {
      if (selectedQuestion) {
        setClosingQuestion(selectedQuestion);

        setSelectedAnswers(prev => ({
          ...prev,
          [selectedQuestion]: null
        }));

        setTimeout(() => {
          setSelectedQuestion(questionId);
          setClosingQuestion(null);
        }, 300);
      } else {
        setSelectedQuestion(questionId);
        setClosingQuestion(null);
      }
    }
  };

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const getAnswerStyle = (question: any, answerIndex: number) => {
    const selectedAnswer = selectedAnswers[question.id];

    if (selectedAnswer === null || selectedAnswer === undefined) {
      return 'glass-interactive-no-active glass-text';
    }

    if (answerIndex === question.correctAnswer) {
      return 'glass-success text-emerald-200';
    }

    if (selectedAnswer === answerIndex && answerIndex !== question.correctAnswer) {
      return 'glass-danger text-rose-200';
    }

    return 'glass-interactive-no-active glass-text';
  };

  const selectedQuestionData = selectedQuestion ?
    questions.find(q => q.id === selectedQuestion) : null;

  return (
    <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
      <div className="app-header-fixed flex items-center">
        <button
          onClick={onBack}
          className="mr-2 sm:mr-3 arrow-button flex-shrink-0"
          aria-label="Назад"
        >
          <ArrowLeft className="arrow-icon" />
        </button>
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0 text-rose-300" />
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-xs landscape:text-sm font-semibold glass-text truncate">Топ ошибок</h1>
        </div>
        <button
          onClick={() => onNavigate('error-top-help')}
          className="ml-2 sm:ml-3 arrow-button flex-shrink-0"
          aria-label="Помощь"
        >
          <HelpCircle className="arrow-icon" />
        </button>
      </div>

      <div className="app-content-scrollable">
        {/* Yandex RTB Banner in Container */}
        <div className="w-full mb-3 sm:mb-4 lg:mb-6 flex justify-center rounded-xl bg-white/5 min-h-[80px]">
          <YandexRTB type="adaptive" idMobile="R-A-17579918-10" idDesktop="R-A-17579918-11" />
        </div>

        {incorrectQuestions.length === 0 ? (
          <div className="text-center py-6 sm:py-8 md:py-12 glass-panel rounded-xl">
            <div className="p-4 sm:p-6 md:p-8">
              <TrendingDown className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 glass-text-muted mx-auto mb-3 sm:mb-4 md:mb-6 glass-icon" />
              <h3 className="font-medium mb-2 sm:mb-3 text-sm sm:text-base md:text-lg glass-text">
                {userStats.totalAnswered > 0 ? 'Отличная работа!' : 'Начните тестирование'}
              </h3>
              <p className="text-xs sm:text-sm md:text-base glass-text-secondary mb-3 sm:mb-4 md:mb-6">
                {userStats.totalAnswered > 0
                  ? 'У вас нет вопросов с ошибками. Продолжайте в том же духе!'
                  : 'Пройдите несколько тестов, чтобы увидеть топ ошибок'
                }
              </p>
              <button
                onClick={() => onNavigate('preparation')}
                className="px-3 py-2 sm:px-4 sm:py-2 md:px-5 md:py-3 glass-interactive-no-active rounded-lg glass-text text-xs sm:text-sm md:text-base"
              >
                Перейти к подготовке
              </button>
            </div>
          </div>
        ) : (
          <>
            <div
              ref={containerRef}
              className="space-y-3 sm:space-y-4 md:space-y-6"
              style={{
                width: containerWidth ? `${containerWidth}px` : '100%',
                maxWidth: '100%',
                minWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              {incorrectQuestions.map((question, index) => {
                if (!question) return null;

                const errorCount = userStats.questionErrors[question.id] || 0;
                const isExpanded = selectedQuestion === question.id;
                const isRemoving = removingItems.has(question.id);

                return (
                  <React.Fragment key={question.id}>
                    <div
                      key={question.id}
                      className={`rounded-xl overflow-hidden ${isRemoving ? 'item-removing' : ''}`}
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: '100%',
                        boxSizing: 'border-box',
                        flexShrink: 0
                      }}
                    >
                      <div
                        className="p-3 sm:p-4 md:p-6 cursor-pointer glass-interactive-no-active rounded-xl"
                        onClick={() => toggleQuestionDetails(question.id)}
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box'
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromIncorrect(question.id);
                          }}
                          disabled={isRemoving}
                          className="p-2 sm:p-3 landscape-compact-p-1 landscape:p-2 glass-interactive rounded-lg glass-text flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            float: 'right',
                            marginLeft: '0.75rem',
                            position: 'relative',
                            zIndex: 10,
                            transition: 'none'
                          }}
                        >
                          <Trash2 className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 landscape:h-4 landscape:w-4 glass-icon" />
                        </button>

                        <div>
                          <p
                            className="mb-2 sm:mb-3 glass-text"
                            style={{
                              fontSize: `${actualQuestionSize}px`,
                              fontFamily: settings.fontFamily,
                              lineHeight: '1.5',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              hyphens: 'auto'
                            }}
                          >
                            {question.question}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center px-2 py-1 rounded-xl text-xs font-medium whitespace-nowrap bg-white/10 backdrop-blur-md border border-white/20 dark:bg-black/30 dark:border-white/15 glass-text">
                              {question.category}
                            </span>

                            <div className="glass-badge-danger whitespace-nowrap badge-no-hover">
                              Ошибок: {errorCount}
                            </div>
                          </div>
                        </div>
                      </div>

                      {(isExpanded || closingQuestion === question.id) && (
                        <div
                          className={`border-t border-white/20 p-3 sm:p-4 md:p-6 glass-interactive-no-active rounded-b-xl ${closingQuestion === question.id ? 'question-details-exit' : 'question-details-enter'
                            }`}
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                          }}
                        >
                          <div
                            className="space-y-2 sm:space-y-3 mb-3 sm:mb-4 md:mb-6"
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              boxSizing: 'border-box'
                            }}
                          >
                            {question.options.map((option, index) => (
                              <button
                                key={index}
                                onClick={() => handleAnswerSelect(question.id, index)}
                                className={`text-left border-2 rounded-lg backdrop-blur-sm answer-option ${getAnswerStyle(question, index)}`}
                                style={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  minWidth: '100%',
                                  padding: '0.75rem 1rem',
                                  boxSizing: 'border-box',
                                  display: 'block',
                                  transition: 'none'
                                }}
                              >
                                <div
                                  className="flex items-center gap-2 sm:gap-3 md:gap-4"
                                  style={{
                                    width: '100%',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box'
                                  }}
                                >
                                  <div
                                    className="rounded-full border-2 border-current flex items-center justify-center text-xs sm:text-sm md:text-base font-medium"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      flexShrink: 0,
                                      boxSizing: 'border-box'
                                    }}
                                  >
                                    {index + 1}
                                  </div>
                                  <span
                                    style={{
                                      fontSize: `${actualAnswerSize}px`,
                                      fontFamily: settings.fontFamily,
                                      lineHeight: '1.5',
                                      wordWrap: 'break-word',
                                      overflowWrap: 'break-word',
                                      hyphens: 'auto',
                                      flex: 1,
                                      minWidth: 0,
                                      maxWidth: '100%',
                                      boxSizing: 'border-box'
                                    }}
                                  >
                                    {option}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>

                          <div className="flex justify-between items-center action-button-enter">
                            <div style={{ width: '48px' }}></div>

                            {question.explanation ? (
                              <button
                                onClick={() => setShowExplanation(true)}
                                className="px-4 py-2 sm:px-5 sm:py-3 md:px-6 md:py-3 text-button-glass glass-text text-sm sm:text-base md:text-lg"
                              >
                                Пояснение
                              </button>
                            ) : (
                              <div></div>
                            )}

                            <button
                              className="p-2 sm:p-3 landscape-compact-p-1 landscape:p-2 glass-interactive rounded-lg glass-text flex items-center justify-center"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(question.id);
                              }}
                              style={{
                                flexShrink: 0,
                                transition: 'none'
                              }}
                            >
                              {favorites.has(question.id) ?
                                <Star className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 landscape:h-4 landscape:w-4 fill-yellow-400 text-yellow-400" /> :
                                <StarOff className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 landscape:h-4 landscape:w-4 glass-icon" />
                              }
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Адаптивная реклама после каждого 5-го вопроса — убрано:
                        дубль blockId делал холостой hit без показа, см. YandexRTB */}
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}

        <Dialog open={showExplanation} onOpenChange={setShowExplanation}>
          <DialogContent className="max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl mx-auto" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl">Пояснение</DialogTitle>
            </DialogHeader>
            <div className="py-1 lg:py-2 xl:py-2">
              {selectedQuestionData?.explanation && (
                <p
                  className="leading-relaxed"
                  style={{
                    fontSize: `${actualAnswerSize}px`,
                    fontFamily: settings.fontFamily,
                    lineHeight: '1.65'
                  }}
                >
                  {selectedQuestionData.explanation}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};